/**
 * Provide a list of free amateur media. A fallback image is inserted if the
 * list ends up empty.
 *
 * @module
 *
 * @import {LightboxItem} from '../../layout/model/lightbox-item'
 *
 * @prettier
 */

export {PARTNER_LIGHTBOX};

import {LocalActor, localValue, Watch} from '@acng/frontend-relativity';
import {AttributeLink, AttributeSetup, NodeUpgrade, UPGRADE} from '@acng/frontend-stargazer';
import {ARRAY, ENUM, guard} from '@acng/frontend-rubicon';
// bounty
import {push, size, unshift} from '@acng/frontend-bounty/std/array.js';
import {IMG, VIDEO} from '@acng/frontend-bounty/dom/element.js';
import {SRC, TYPE} from '@acng/frontend-bounty/dom/attribute.js';
import {CLICK, on} from '@acng/frontend-bounty/dom/event.js';
import {add} from '@acng/frontend-bounty/style/element.js';
import {URL} from '@acng/frontend-bounty/net/url.js';
/**
 * @import {Amateur} from '../model/amateur.js';
 */
import {media} from '../../core/service/env.js';
import {Actor} from '../../layout/context/list.js';
import {ctxLightboxItem} from '../../layout/context/lightbox-item.js';
import {user} from '../../user/user.js';
import {ctxAmateur} from '../context/amateur.js';
import {STYLE_ACTIVE, STYLE_EMPTY, STYLE_INACTIVE, swapClass} from '../../layout/service/style.js';
import {IMAGE_FORMAT as FRIGG_FORMAT} from '../../core/model/image.js';
import {getFallbackImageUrl} from '../../layout/service/fallback-image.js';

/**
 * @group DOM Attribute
 */
const PARTNER_LIGHTBOX = 'partner-lightbox';

AttributeSetup(PARTNER_LIGHTBOX, (name) => {
  /**
   * Loading preview when the user came from the amateur list to the sedcard. To
   * show no blank screen while loading the needed image.
   */
  const CACHED_FORMAT = '800';
  const IMAGE_FORMAT = '1200';
  const watch = Watch(`[${name}]`, ctxAmateur);
  const broadcast = Actor();
  const provideItem = LocalActor(ctxLightboxItem);

  /**
   * @param {Element} element
   * @param {Amateur | null} amateur
   * @param {string} value
   *
   * @returns {LightboxItem[]}
   */
  const setup = (element, amateur, value) => {
    const format = value !== '' ? value.split(',') : ['800', '1200'];
    ASSERT: guard(format, ARRAY(FRIGG_FORMAT));
    /**
     * @type {LightboxItem | null}
     */
    let preferred = null;

    /**
     * @type {LightboxItem[]}
     */
    const result = [];
    const fskLevel = user.fsk;

    if (amateur) {
      for (const [, fsk, src] of amateur.images) {
        if (fsk > fskLevel) {
          continue;
        }

        /**
         * @type {LightboxItem}
         */
        const item = {
          [TYPE]: IMG,
          thumbnail: `${new URL(
            `${src}-${preferred ? format[0] : CACHED_FORMAT}.webp`,
            media.content.pictures,
          )}`,
          [SRC]: `${new URL(`${src}-${format[1]}.webp`, media.content.pictures)}`,
          format: format[1],
        };

        preferred ??= item;
        push(result, item);
      }

      if (amateur.profile_video_path && (amateur.profile_video_fsk ?? 18) <= fskLevel) {
        unshift(result, {
          [TYPE]: VIDEO,
          thumbnail: amateur.getImageUrl('800'),
          [SRC]: `${new URL(amateur.profile_video_path, media.content.movies)}`,
          format: IMAGE_FORMAT,
        });
      }

      if (!size(result)) {
        preferred = {
          [TYPE]: IMG,
          thumbnail: getFallbackImageUrl(format[0]),
          [SRC]: getFallbackImageUrl(format[1]),
          format: format[1],
        };

        push(result, preferred);
      }
    }

    provideItem(element, preferred);
    swapClass(element, STYLE_EMPTY, size(result) <= 1);

    // whitelist filter for image or video.
    // Current possible values: alternate-img video preferred-img
    // List is comma separated
    const listFilter = element.getAttribute('list-filter');
    if (listFilter) {
      const filters = listFilter.split(',').map((f) => f.trim());
      ASSERT: guard(filters, ARRAY(ENUM(/** @type {const} */ (['alternate-img', 'video', 'preferred-img']))));
      const filteredResult = result.filter((item) => {
        if (item.type === IMG && filters.includes('alternate-img') && item !== preferred) {
          return true;
        }
        if (item.type === VIDEO && filters.includes('video')) {
          return true;
        }
        if (item === preferred && filters.includes('preferred-img')) {
          return true;
        }
        return false;
      });
      return filteredResult;
    }
    return result;
  };

  AttributeLink(name, (element, value) => {
    watch(element, (element, amateur) => {
      /**
       * @type {HTMLElement | null}
       */
      let selectedElement = null;

      const list = setup(element, amateur, value);

      broadcast(
        element,
        async (offset) => (offset ? [] : list),
        async (item) => (itemElement, engine) => {
          if (item == localValue(element, ctxLightboxItem)) {
            add(itemElement, STYLE_ACTIVE);
            selectedElement = itemElement;
          } else {
            add(itemElement, STYLE_INACTIVE);
          }

          add(itemElement, `lightbox-${item.type}`);
          provideItem(itemElement, item);

          NodeUpgrade(engine, {
            ...item,
            /**
             * @type {import('@acng/frontend-stargazer').Controller}
             */
            click: {
              [UPGRADE](node) {
                on(node, CLICK, () => {
                  if (selectedElement == itemElement) {
                    return;
                  }

                  if (selectedElement) {
                    swapClass(selectedElement, STYLE_INACTIVE);
                  }

                  selectedElement = itemElement;
                  provideItem(element, item);
                  swapClass(itemElement, STYLE_ACTIVE);
                });
              },
            },
          });
        },
      );
    });
  });
});
